import React, { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Chart from 'chart.js';
import { InfoTooltip } from '@wwe-portal/ui/components/tooltip/info-tooltip';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';
import { tryFormatThousands } from '@wwe-portal/utils/format';

interface FactionChartsProps {
  chart: {
    title: string;
    firstIcon?: string;
    firstIconTitle: string;
    secondIcon: string;
    secondIconTitle: string;
    gradientZero: string;
    data: number[];
    borderColor: string;
    id: number;
  };
  xLabels: string[];
  isSuperstar?: boolean;
  isTotalUpgrade?: boolean;
}

const FactionCharts: React.FC<FactionChartsProps> = ({
  chart: {
    title,
    firstIcon,
    firstIconTitle,
    secondIcon,
    secondIconTitle,
    gradientZero,
    data,
    borderColor,
    id,
  },
  xLabels,
  isSuperstar = false,
  isTotalUpgrade = false,
}) => {
  const t = useTranslations();

  useEffect(() => {
    const ctx = (
      document.getElementById(`myChart${id}`) as HTMLCanvasElement
    ).getContext('2d');
    const gradient = ctx?.createLinearGradient(0, 0, 0, 300);

    gradient?.addColorStop(0, gradientZero);
    gradient?.addColorStop(1, 'rgba(33, 37, 65, 0.1)');

    if (ctx) {
      // eslint-disable-next-line sonarjs/constructor-for-side-effects
      new Chart(ctx, {
        type: 'line',
        data: {
          labels: xLabels,
          datasets: [
            {
              data: data,
              backgroundColor: gradient,
              borderColor: borderColor,
              borderWidth: 1.5,
              pointBackgroundColor: borderColor,
            },
          ],
        },
        options: {
          legend: {
            display: false,
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false,
                },
                stacked: true,
                ticks: {
                  fontColor: '#4f5794',
                  fontSize: 14,
                  beginAtZero: true,
                },
              },
            ],
            yAxes: [
              {
                stacked: true,
                gridLines: {
                  offsetGridLines: false,
                  display: true,
                  borderDash: [6, 2],
                  tickMarkLength: 5,
                  color: '#343961',
                  zeroLineWidth: 0,
                },
                ticks: {
                  beginAtZero: false,
                  fontColor: '#4f5794',
                  fontSize: 14,
                  callback: function (value) {
                    return tryFormatThousands(value);
                  },
                },
              },
            ],
          },
        },
      });
    }
  }, [id, data, xLabels, gradientZero, borderColor]);

  const firstFormatter = tryFormatThousands(firstIconTitle);
  const secondFormatter = tryFormatThousands(secondIconTitle);

  return (
    <div className="bg-back400 hidden w-full rounded p-5 md:block">
      <div className="faction-chart-header flex justify-between pt-[10px]">
        <div>
          <h5 className="text-text400 m-0 p-0">{t(title)}</h5>
          <div className="text-capitalize text-text600 m-0 flex items-center justify-between p-0 text-sm">
            {t('last')} {t('12_month')}
            {isTotalUpgrade ? (
              <InfoTooltip content={t('superstar_chart_tooltip_desc')} />
            ) : null}
          </div>
        </div>
        <div className="faction-chart-items flex justify-between">
          <div className="faction-chart-items-item flex items-center justify-center">
            <span
              className="faction-chart-items-item-icon border-back200 mr-[15px] rounded-md border p-[5px]"
              style={{
                color: borderColor,
              }}
            >
              <IcoMoonIcon iconName={firstIcon || ''} className="size-4" />
            </span>
            <div className="pr-[15px]">
              <h5 className="m-0 p-0 font-medium">{firstFormatter}</h5>
              <p className="text-text600 m-0 p-0 text-sm">
                {isSuperstar ? t('last_30_day') : t('this_month')}
              </p>
            </div>
          </div>
          <div className="faction-chart-items-item flex justify-center pl-[15px]">
            <span
              className="faction-chart-items-item-icon border-back200 m-auto mr-[15px] rounded-md border p-[5px]"
              style={{
                color: borderColor,
              }}
            >
              <IcoMoonIcon iconName={secondIcon || ''} className="size-4" />
            </span>
            <div className="pr-[15px]">
              <h5 className="m-0 p-0 font-medium">{secondFormatter}</h5>
              <p className="text-text600 m-0 h-[1px] p-0 text-sm">
                {t('All_time')}
              </p>
            </div>
          </div>
        </div>
      </div>
      <hr className="border-borderColor7 my-[30px] border" />
      <canvas
        id={`myChart${id}`}
        width="550px"
        height="260px"
        style={{ maxWidth: '100%', maxHeight: '100%' }}
      />
    </div>
  );
};

export default FactionCharts;
